/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import style from './Searchbar.scss?inline';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { useCSS } from '../../../provider/CSSProvider';
import { useLocation, useNavigate } from 'react-router';
import { OptionChildType, OptionFieldType, OptionType, searchOptions } from './Entries';
import SearchIcon from '@mui/icons-material/Search';
import { getPageRoute, PageType } from '../../../models/diviPage';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';

interface SearchEntry {
  label: string;
  path: string;
  keywords?: string[] | undefined;
  group: string;
}

function Searchbar() {
  useCSS(style);

  const navigate = useNavigate();
  const location = useLocation();
  const { acronyms } = useContext(ReportsAPIContext);
  const [valueState, setValueState] = useState<string | SearchEntry | null>('');
  const [inputValueState, setInputValueState] = useState<string>('');
  const autocompleteRef = useRef<HTMLElement | null>(null);

  //needed in order to scroll to the card id
  useEffect(() => {
    // Delay scrolling for React rendering to complete first
    setTimeout(() => {
      const anchor = '#card-' + location.hash.replace('#', '');
      if (anchor) {
        const root = autocompleteRef.current?.getRootNode() as Document | ShadowRoot | undefined;
        const element = root?.querySelector(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 0);
  }, [location]);

  const filterOptions = (options: SearchEntry[], { inputValue }: { inputValue: string }): SearchEntry[] => {
    return options.filter((option) => {
      const searchKeyword = inputValue.toLowerCase();
      if (option.label.toLowerCase().includes(searchKeyword)) {
        return true;
      }
      return option.keywords?.some((keyword) => keyword.toLowerCase().includes(searchKeyword));
    });
  };

  const handleSearchSelect = (event: React.SyntheticEvent<Element, Event>, value: string | SearchEntry | null) => {
    if (value != null && typeof value !== 'string') {
      navigate(value.path);
      setValueState('');
      setInputValueState('');
    } else {
      setValueState(value);
    }
  };

  const handleInputChange = (event: React.SyntheticEvent<Element, Event>, value: string) => {
    setInputValueState(value);
  };

  function generateFlatList(options: OptionType[], acronymOptions: OptionType[]) {
    const flatList: SearchEntry[] = [];

    function addEntries(option: OptionType | OptionFieldType | OptionChildType, basePath: string, group: string) {
      const newPath = 'cardId' in option ? `${basePath}#${option.cardId}` : basePath;
      flatList.push({
        label: option.label,
        path: newPath,
        keywords: option.keywords,
        group: group,
      });

      if ('fields' in option && option.fields) {
        option.fields.forEach((field) => addEntries(field, newPath, group));
      } else if ('children' in option && option.children) {
        option.children.forEach((child) => addEntries(child, newPath, group));
      }
    }
    [...options, ...acronymOptions].forEach((option) => addEntries(option, option.path, option.label));
    return flatList;
  }

  function getOptionKey(option: string | SearchEntry): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.path + option.label;
  }

  function generateAcronymSearchOptions(): OptionType[] {
    if (acronyms == null) {
      return [];
    }

    return Object.getOwnPropertyNames(acronyms).map((key: string) => {
      return {
        label: 'Akronym - ' + key,
        path: getPageRoute(PageType.Acronym) + '/' + encodeURIComponent(key),
      };
    });
  }

  const acronymOptions: OptionType[] = generateAcronymSearchOptions();
  const flatList = useMemo(() => generateFlatList(searchOptions, acronymOptions), [acronymOptions]);

  return (
    <Autocomplete
      disablePortal
      freeSolo
      className='searchbar'
      options={flatList}
      groupBy={(option) => option.group}
      getOptionKey={getOptionKey}
      value={valueState}
      inputValue={inputValueState}
      onInputChange={handleInputChange}
      onChange={handleSearchSelect}
      getOptionLabel={(option: string | SearchEntry) => (typeof option === 'string' ? option : option.label)}
      size={'small'}
      ref={autocompleteRef}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder='Suche nach Schlagwörtern oder Akronymen ...'
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      filterOptions={filterOptions}
    />
  );
}

export default Searchbar;
